import React from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Eyebrow } from '../components/ui/Eyebrow';
import { labPanels } from '../data/site';
import { useBrand } from '../contexts/BrandContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
const useful = [{
  marker: 'TSH, free T4',
  why: 'Thyroid disease mimics perimenopause almost exactly and is completely treatable. This is the single most important thing to rule out.'
}, {
  marker: 'Ferritin',
  why: 'Heavy perimenopausal bleeding causes iron deficiency far more often than it is caught. It produces exhaustion and brain fog on its own.'
}, {
  marker: 'HbA1c, fasting insulin',
  why: 'Insulin resistance rises through the transition and drives fatigue, weight redistribution and cardiovascular risk.'
}, {
  marker: 'Lipids, ApoB',
  why: 'Cardiovascular risk changes as estrogen falls. ApoB is the more informative number and is rarely offered.'
}, {
  marker: 'Vitamin D',
  why: 'Deficiency is common, contributes to bone loss and muscle weakness, and is cheap to correct — but only if you know your level.'
}];
const theatre = [{
  marker: 'A single estradiol level',
  why: 'In perimenopause estradiol swings from higher than it has ever been to very low, sometimes within one week. One draw tells you about that morning.'
}, {
  marker: 'FSH over 45',
  why: 'Guidelines are explicit: for women over 45 with typical symptoms, no hormone testing is required to diagnose perimenopause. A normal FSH rules nothing out.'
}, {
  marker: 'Salivary hormone panels',
  why: 'Not validated against clinical outcomes. Widely sold, particularly alongside compounded hormone products.'
}, {
  marker: '“Full hormone panels” at intake',
  why: 'They look thorough and they generate a lot of numbers. They very rarely change what a clinician would do next.'
}];
export function Testing() {
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Testing — ${brand.name}`,
    description: 'Why a blood test cannot diagnose perimenopause, which markers actually change the plan, and which ones are theatre.'
  });
  return <main className="w-full bg-cream" data-id="element-735" data-mp="2o2bqoljmj8wl">
      <PageHeader eyebrow="Testing" title="A blood test cannot diagnose perimenopause." intro="We could sell you a large hormone panel. It would look impressive and it would not change what we do next. Here is what we test instead, and exactly why — including the tests we deliberately do not run." data-id="element-736" data-mp="2o3d24r4jxxeu" />

      <section className="bg-cream" data-id="element-737" data-mp="259zp410sobni">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-24 md:pt-14" data-id="element-738" data-mp="258y3j2m5k6rj">
          <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20" data-id="element-739" data-mp="3lnhfjdnyvqkw">
            <div data-id="element-740" data-mp="3gqha94yxyruu">
              <h2 className="max-w-2xl font-serif text-4xl leading-[1.06] text-ink md:text-[3rem]" data-id="element-741" data-mp="3gowgviod5eyk">
                “Your bloods came back normal” is not the reassurance it sounds like.
              </h2>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-742" data-mp="17iky8physet6">
                During perimenopause, hormone levels do not decline in a smooth line. Estradiol
                spikes higher than at any point in your reproductive life, then crashes. Draw blood
                on a high day and everything looks unremarkable. Draw it four days later and it looks
                dramatic. Neither result changes what is happening to you.
              </p>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-743" data-mp="1cgw6ivsm2h9a">
                Perimenopause is a clinical diagnosis. It is made from your age, your cycle history,
                your symptom pattern, and the exclusion of the conditions that look like it. If
                someone has told you a normal result rules it out, they are using a test outside the
                conditions in which it works.
              </p>
            </div>
            <img src={brand.images.labKit} alt="A blood collection kit laid out on a linen surface in low light." className="h-72 w-full rounded-brand object-cover md:h-full" data-id="element-744" data-mp="l7b7gvbsmxcb" />
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-745" data-mp="7bqushmnrbdc">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-746" data-mp="140osa7lslne7">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20" data-id="element-747" data-mp="141zuw43i4p3q">
            <div data-id="element-748" data-mp="zz3l92vxw3bx">
              <Eyebrow className="text-brick" data-id="element-749" data-mp="100oemp6ipg87">What we do test</Eyebrow>
              <h2 className="mt-5 font-serif text-3xl leading-snug text-ink" data-id="element-750" data-mp="v6k5q0doyy4h">
                Markers that change the plan.
              </h2>
              <ul className="mt-8" data-id="element-751" data-mp="q6xq5kae55si">
                {useful.map(item => <li key={item.marker} className="rule py-5" data-id="element-752" data-mp="1nph5w8c72zwh">
                    <p className="font-serif text-xl text-ink" data-id="element-753" data-mp="1iweh0did4emm">{item.marker}</p>
                    <p className="mt-2 leading-relaxed text-ink-soft" data-id="element-754" data-mp="1q93xrfxuaw6x">{item.why}</p>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-755" data-mp="1v9rqc61pvl39">
              <Eyebrow className="text-ink-muted" data-id="element-756" data-mp="206rv8dmtyhss">What we don’t</Eyebrow>
              <h2 className="mt-5 font-serif text-3xl leading-snug text-ink" data-id="element-757" data-mp="208uulo9vhna6">
                Markers that mostly generate numbers.
              </h2>
              <ul className="mt-8" data-id="element-758" data-mp="29886sopdspyl">
                {theatre.map(item => <li key={item.marker} className="rule py-5" data-id="element-759" data-mp="2e69nx438yf7f">
                    <p className="font-serif text-xl text-ink-muted line-through decoration-brick/50 decoration-1" data-id="element-760" data-mp="2e4oq95lmyg5c">
                      {item.marker}
                    </p>
                    <p className="mt-2 leading-relaxed text-ink-soft" data-id="element-761" data-mp="2nv1d54gxalen">{item.why}</p>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-762" data-mp="2wxsnf0y2bb4p">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-763" data-mp="2wuoh9y11e5be">
          <SectionHeading eyebrow="Panels" title="Three panels. That is the whole list." intro="Drawn at a partner site near you, or at home with a kit. Results are explained by a clinician in plain language, and you keep a copy to take anywhere." data-id="element-764" data-mp="wv18lsaefm28" />
          <ul className="mt-14 grid gap-px overflow-hidden rounded-brand border border-ink/15 bg-ink/15 lg:grid-cols-3" data-id="element-765" data-mp="11sjlftrjelfi">
            {labPanels.map(panel => <li key={panel.name} className="flex flex-col bg-cream-light p-8" data-id="element-766" data-mp="1bkgxt3ua7jft">
                <h3 className="font-serif text-2xl leading-snug text-ink" data-id="element-767" data-mp="1pj6z6vo4w59z">{panel.name}</h3>
                <p className="mt-4 leading-relaxed text-ink-soft" data-id="element-768" data-mp="1kkltwhu0yveo">{panel.detail}</p>
                <div className="rule mt-auto flex items-baseline justify-between gap-4 pt-6" data-id="element-769" data-mp="1kij8yc7qbsgt">
                  <p className="font-serif text-3xl text-ink" data-id="element-770" data-mp="3rcmyegyaesgk">${panel.price}</p>
                  <p className="text-sm text-ink-soft" data-id="element-771" data-mp="3mdjsyveeak82">{panel.markers} markers</p>
                </div>
              </li>)}
          </ul>
          <p className="mt-8 max-w-3xl text-ink-soft" data-id="element-772" data-mp="26tasvnqbsthu">
            The baseline screen is included with membership. If you have had any of these run in the
            last twelve months, send us the results and we will not repeat them.
          </p>
        </div>
      </section>

      <CtaBand data-id="element-773" data-mp="1j36qtkssd1sy" />
    </main>;
}